import React, { useState, useEffect } from 'react';
import {
    Mail, Send, Users, Plus, Edit2, Trash2, Eye,
    BarChart3, FileText, Calendar, Clock, User,
    CheckCircle, XCircle, AlertCircle, RefreshCw,
    Upload, MessageSquare, UserPlus, Star
} from 'lucide-react';

import {
    fetchEmailCampaigns, fetchEmailAnalytics, createEmailCampaign, updateEmailCampaign,
    deleteEmailCampaign, sendEmailCampaign, addRecipientsToEmailCampaign,
    addRecipientsFromContacts, validateEmailCampaign, parseRecipientsFromText,

    fetchEmailSenders, createEmailSender, updateEmailSender, deleteEmailSender, setDefaultSender,

    fetchEmailContacts, createEmailContact, updateEmailContact, deleteEmailContact,
    bulkImportContacts, fetchContactSegments, fetchContactIndustries, fetchContactTags,
    parseContactsFromText,

    fetchQuickEmails, createQuickEmail, sendQuickEmail, sendQuickEmailDirect,
    updateQuickEmail, deleteQuickEmail, validateQuickEmail,

    getEmailCampaignTemplates
} from '../../services/emailCampaignService';
import {
    trackEmailEvent,
    trackEmailConversion,
    getCurrentEmailUserData,
    addEmailOpenTracking
} from '../AnalyticsTracker';


const EmailCampaignManager = ({ token }) => {
    const [activeTab, setActiveTab] = useState('dashboard');

    // Основные данные
    const [campaigns, setCampaigns] = useState([]);
    const [analytics, setAnalytics] = useState({});
    const [senders, setSenders] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [quickEmails, setQuickEmails] = useState([]);

    // Состояние загрузки и ошибок
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Фильтры и поиск
    const [contactFilters, setContactFilters] = useState({
        search: '',
        segment: '',
        industry: '',
        status: '',
        tags: ''
    });
    const [segments, setSegments] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [setTags] = useState([]);

    // Модальные окна и формы
    const [showCampaignModal, setShowCampaignModal] = useState(false);
    const [showQuickEmailModal, setShowQuickEmailModal] = useState(false);
    const [showSenderModal, setShowSenderModal] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [showRecipientsModal, setShowRecipientsModal] = useState(false);
    const [showContactImportModal, setShowContactImportModal] = useState(false);

    // Выбранные элементы
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    const [recipientsMode, setRecipientsMode] = useState('manual'); // 'manual' или 'contacts'

    // Формы
    const [campaignForm, setCampaignForm] = useState({
        name: '',
        campaign_type: 'cold_outreach',
        subject: '',
        html_content: '',
        text_content: '',
        sender: '',
        utm_campaign: '',
        utm_content: '',
        recipients_data: []
    });

    const [quickEmailForm, setQuickEmailForm] = useState({
        sender: '',
        recipient_email: '',
        recipient_name: '',
        subject: '',
        html_content: '',
        text_content: ''
    });

    const [senderForm, setSenderForm] = useState({
        name: '',
        email: '',
        display_name: '',
        is_default: false
    });

    const [contactForm, setContactForm] = useState({
        email: '',
        name: '',
        company: '',
        phone: '',
        position: '',
        website: '',
        industry: '',
        segment: '',
        tags: '',
        notes: ''
    });

    const [recipientsText, setRecipientsText] = useState('');
    const [contactImportText, setContactImportText] = useState('');

    const campaignTypes = {
        cold_outreach: 'Холодная рассылка',
        technical_newsletter: 'Техническая рассылка',
        product_update: 'Обновления продукта',
        reactivation: 'Реактивация',
        custom: 'Пользовательская'
    };

    const statusColors = {
        draft: 'bg-gray-100 text-gray-800',
        scheduled: 'bg-blue-100 text-blue-800',
        sending: 'bg-yellow-100 text-yellow-800',
        sent: 'bg-green-100 text-green-800',
        failed: 'bg-red-100 text-red-800'
    };

    const statusIcons = {
        draft: FileText,
        scheduled: Calendar,
        sending: Clock,
        sent: CheckCircle,
        failed: XCircle
    };

    useEffect(() => {
        const emailUserData = getCurrentEmailUserData();
        if (emailUserData.isEmailUser) {
            trackEmailEvent('email_manager_view', {
                campaign_source: emailUserData.source,
                lead_id: emailUserData.leadId,
                page_section: 'email_campaign_manager'
            });
        }

        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab === 'contacts') {
            loadContacts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, contactFilters]);

    const loadInitialData = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const [
                campaignsData,
                analyticsData,
                sendersData,
                quickEmailsData,
                segmentsData,
                industriesData,
                tagsData
            ] = await Promise.all([
                fetchEmailCampaigns(token),
                fetchEmailAnalytics(token),
                fetchEmailSenders(token),
                fetchQuickEmails(token),
                fetchContactSegments(token),
                fetchContactIndustries(token),
                fetchContactTags(token)
            ]);

            setCampaigns(campaignsData);
            setAnalytics(analyticsData);
            setSenders(sendersData);
            setQuickEmails(quickEmailsData);
            setSegments(segmentsData);
            setIndustries(industriesData);
            setTags(tagsData);

        } catch (err) {
            setError('Ошибка при загрузке данных: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const loadContacts = async () => {
        try {
            const contactsData = await fetchEmailContacts(token, contactFilters);
            setContacts(contactsData);
        } catch (err) {
            setError('Ошибка при загрузке контактов: ' + err.message);
        }
    };

    const showSuccessMessage = (message) => {
        setSuccess(message);
        setTimeout(() => setSuccess(null), 5000);
    };

    const showErrorMessage = (message) => {
        setError(message);
        setTimeout(() => setError(null), 5000);
    };

    const resetCampaignForm = () => {
        setCampaignForm({
            name: '',
            campaign_type: 'cold_outreach',
            subject: '',
            html_content: '',
            text_content: '',
            sender: senders.find(s => s.is_default)?.id?.toString() || '',
            utm_campaign: '',
            utm_content: '',
            recipients_data: []
        });
    };

    const resetQuickEmailForm = () => {
        setQuickEmailForm({
            sender: senders.find(s => s.is_default)?.id?.toString() || '',
            recipient_email: '',
            recipient_name: '',
            subject: '',
            html_content: '',
            text_content: ''
        });
    };

    // Обработчики для кампаний
    const handleCreateCampaign = () => {
        setEditingItem(null);
        resetCampaignForm();
        setShowCampaignModal(true);

        trackEmailEvent('campaign_creation_started', {
            event_category: 'email_marketing',
            action_type: 'create'
        });
    };

    const handleEditCampaign = (campaign) => {
        setEditingItem(campaign);
        setCampaignForm({
            name: campaign.name,
            campaign_type: campaign.campaign_type,
            subject: campaign.subject,
            html_content: campaign.html_content,
            text_content: campaign.text_content,
            sender: campaign.sender?.toString() || '',
            utm_campaign: campaign.utm_campaign,
            utm_content: campaign.utm_content,
            recipients_data: []
        });
        setShowCampaignModal(true);

        trackEmailEvent('campaign_edit_started', {
            campaign_id: campaign.id,
            campaign_type: campaign.campaign_type,
            event_category: 'email_marketing'
        });
    };

    const handleSaveCampaign = async () => {
        const errors = validateEmailCampaign(campaignForm);
        if (errors.length > 0) {
            showErrorMessage(errors.join(', '));
            return;
        }

        let htmlContent = campaignForm.html_content;
        if (htmlContent && !htmlContent.includes('email-tracking')) {
            const campaignTrackingId = `campaign_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const trackingPixel = addEmailOpenTracking(campaignTrackingId);
            htmlContent = htmlContent + trackingPixel;
        }

        const campaignData = {
            ...campaignForm,
            html_content: htmlContent,
            sender: campaignForm.sender ? parseInt(campaignForm.sender, 10) : null
        };

        setIsLoading(true);
        try {
            if (editingItem) {
                await updateEmailCampaign(token, editingItem.id, campaignData);
                showSuccessMessage('Кампания обновлена');

                trackEmailEvent('campaign_updated', {
                    campaign_id: editingItem.id,
                    campaign_type: campaignData.campaign_type,
                    event_category: 'email_marketing'
                });
            } else {
                await createEmailCampaign(token, campaignData);
                showSuccessMessage('Кампания создана');

                trackEmailEvent('campaign_created', {
                    campaign_type: campaignData.campaign_type,
                    event_category: 'email_marketing',
                    utm_campaign: campaignData.utm_campaign
                });
            }

            setShowCampaignModal(false);
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при сохранении кампании: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Обработчики для быстрых писем
    const handleCreateQuickEmail = () => {
        setEditingItem(null);
        resetQuickEmailForm();
        setShowQuickEmailModal(true);

        trackEmailEvent('quick_email_creation_started', {
            event_category: 'email_marketing',
            action_type: 'create'
        });
    };

    const handleSaveQuickEmail = async () => {
        const errors = validateQuickEmail(quickEmailForm);
        if (errors.length > 0) {
            showErrorMessage(errors.join(', '));
            return;
        }

        const emailData = {
            ...quickEmailForm,
            sender: quickEmailForm.sender ? parseInt(quickEmailForm.sender, 10) : null
        };

        setIsLoading(true);
        try {
            if (editingItem) {
                await updateQuickEmail(token, editingItem.id, emailData);
                showSuccessMessage('Письмо обновлено');
            } else {
                await createQuickEmail(token, emailData);
                showSuccessMessage('Письмо создано');
            }

            setShowQuickEmailModal(false);
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при сохранении письма: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendQuickEmailDirect = async () => {
        const errors = validateQuickEmail(quickEmailForm);
        if (errors.length > 0) {
            showErrorMessage(errors.join(', '));
            return;
        }

        let htmlContent = quickEmailForm.html_content;
        if (htmlContent) {
            const emailTrackingId = `quick_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const trackingPixel = addEmailOpenTracking(emailTrackingId);
            htmlContent = htmlContent + trackingPixel;
        }

        const emailData = {
            ...quickEmailForm,
            html_content: htmlContent,
            sender: quickEmailForm.sender ? parseInt(quickEmailForm.sender, 10) : null
        };

        setIsLoading(true);
        try {
            await sendQuickEmailDirect(token, emailData);
            showSuccessMessage('Письмо отправлено');

            trackEmailEvent('quick_email_sent', {
                recipient_email: emailData.recipient_email,
                sender_id: emailData.sender,
                event_category: 'email_marketing'
            });

            setShowQuickEmailModal(false);
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при отправке письма: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Обработчики для отправителей
    const handleCreateSender = () => {
        setEditingItem(null);
        setSenderForm({
            name: '',
            email: '',
            display_name: '',
            is_default: false
        });
        setShowSenderModal(true);
    };

    const handleSaveSender = async () => {
        if (!senderForm.name || !senderForm.email) {
            showErrorMessage('Имя и email отправителя обязательны');
            return;
        }

        setIsLoading(true);
        try {
            if (editingItem) {
                await updateEmailSender(token, editingItem.id, senderForm);
                showSuccessMessage('Отправитель обновлен');
            } else {
                await createEmailSender(token, senderForm);
                showSuccessMessage('Отправитель создан');
            }

            setShowSenderModal(false);
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при сохранении отправителя: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Обработчики для контактов
    const handleCreateContact = () => {
        setEditingItem(null);
        setContactForm({
            email: '',
            name: '',
            company: '',
            phone: '',
            position: '',
            website: '',
            industry: '',
            segment: '',
            tags: '',
            notes: ''
        });
        setShowContactModal(true);
    };



    const handleSendCampaign = async (campaignId) => {
        if (!window.confirm('Вы уверены, что хотите отправить эту кампанию?')) {
            return;
        }

        const campaign = campaigns.find(c => c.id === campaignId);

        setIsLoading(true);
        try {
            await sendEmailCampaign(token, campaignId);
            showSuccessMessage('Кампания запущена в отправку');

            trackEmailEvent('campaign_sent', {
                campaign_id: campaignId,
                campaign_type: campaign?.campaign_type,
                recipients_count: campaign?.recipients_count || 0,
                event_category: 'email_marketing',
                utm_campaign: campaign?.utm_campaign
            });

            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при отправке кампании: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const trackCampaignConversion = (conversionType, campaignId = null, value = null) => {
        const emailUserData = getCurrentEmailUserData();

        trackEmailConversion(conversionType, value);

        trackEmailEvent('campaign_conversion', {
            conversion_type: conversionType,
            campaign_id: campaignId,
            conversion_value: value,
            lead_id: emailUserData.leadId,
            email_id: emailUserData.emailId,
            event_category: 'conversions'
        });
    };

    const handleSaveContact = async () => {
        if (!contactForm.email) {
            showErrorMessage('Email контакта обязателен');
            return;
        }

        setIsLoading(true);
        try {
            if (editingItem) {
                await updateEmailContact(token, editingItem.id, contactForm);
                showSuccessMessage('Контакт обновлен');

                trackEmailEvent('contact_updated', {
                    contact_id: editingItem.id,
                    contact_segment: contactForm.segment,
                    contact_industry: contactForm.industry,
                    event_category: 'contact_management'
                });
            } else {
                await createEmailContact(token, contactForm);
                showSuccessMessage('Контакт создан');

                trackCampaignConversion('contact_created', null, 1);

                trackEmailEvent('contact_created', {
                    contact_segment: contactForm.segment,
                    contact_industry: contactForm.industry,
                    event_category: 'contact_management'
                });
            }

            setShowContactModal(false);
            loadContacts();
        } catch (err) {
            showErrorMessage('Ошибка при сохранении контакта: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBulkImportContacts = async () => {
        if (!contactImportText.trim()) {
            showErrorMessage('Введите данные контактов для импорта');
            return;
        }

        const contactsData = parseContactsFromText(contactImportText);
        if (contactsData.length === 0) {
            showErrorMessage('Не удалось распознать контакты из введенного текста');
            return;
        }

        setIsLoading(true);
        try {
            const result = await bulkImportContacts(token, contactsData, true);
            showSuccessMessage(`Импорт завершен. Создано: ${result.created_count}, обновлено: ${result.updated_count}`);

            trackCampaignConversion('contacts_imported', null, result.created_count);

            trackEmailEvent('contacts_bulk_imported', {
                imported_count: contactsData.length,
                created_count: result.created_count,
                updated_count: result.updated_count,
                event_category: 'contact_management'
            });

            setShowContactImportModal(false);
            setContactImportText('');
            loadContacts();
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при импорте контактов: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);

        trackEmailEvent('email_manager_tab_view', {
            tab_name: newTab,
            previous_tab: activeTab,
            event_category: 'navigation'
        });

        const emailUserData = getCurrentEmailUserData();
        if (emailUserData.isEmailUser) {
            trackEmailEvent('email_user_tab_interaction', {
                tab_name: newTab,
                lead_id: emailUserData.leadId,
                campaign_source: emailUserData.source,
                event_category: 'email_marketing'
            });
        }
    };

    // Загрузка шаблонов
    const loadCampaignTemplate = (campaignType) => {
        const templates = getEmailCampaignTemplates();
        const template = templates[campaignType];

        if (template) {
            setCampaignForm(prev => ({
                ...prev,
                subject: template.subject,
                html_content: template.html_content,
                text_content: template.text_content,
                utm_campaign: prev.name.toLowerCase().replace(/\s+/g, '_')
            }));

            trackEmailEvent('template_used', {
                template_type: campaignType,
                campaign_name: campaignForm.name,
                event_category: 'email_marketing'
            });
        }
    };

    const loadQuickEmailTemplate = (templateType) => {
        const templates = getEmailCampaignTemplates();
        const template = templates[templateType];

        if (template) {
            const senderName = senders.find(s => s.id === parseInt(quickEmailForm.sender))?.display_name || '';
            setQuickEmailForm(prev => ({
                ...prev,
                subject: template.subject.replace('{{sender_name}}', senderName),
                html_content: template.html_content.replace(/{{sender_name}}/g, senderName),
                text_content: template.text_content.replace(/{{sender_name}}/g, senderName)
            }));
        }
    };

    const renderDashboard = () => (
        <div className="space-y-6 text-white">
            {/* Основные метрики */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gray-800 rounded-lg shadow p-6 border border-gray-700">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-600 rounded-lg">
                            <Mail className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-300">Всего кампаний</p>
                            <p className="text-2xl font-bold text-white">{analytics.total_campaigns || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-800 rounded-lg shadow p-6 border border-gray-700">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-600 rounded-lg">
                            <Send className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-300">Отправлено писем</p>
                            <p className="text-2xl font-bold text-white">{analytics.total_emails_sent || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-800 rounded-lg shadow p-6 border border-gray-700">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-600 rounded-lg">
                            <Users className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-300">Контакты</p>
                            <p className="text-2xl font-bold text-white">{analytics.contacts_count || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-800 rounded-lg shadow p-6 border border-gray-700">
                    <div className="flex items-center">
                        <div className="p-2 bg-yellow-600 rounded-lg">
                            <Eye className="h-6 w-6 text-white" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-300">Средний Open Rate</p>
                            <p className="text-2xl font-bold text-white">{analytics.avg_open_rate?.toFixed(1) || 0}%</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ДОБАВЛЕНО: Блок с информацией о текущем email пользователе */}
            {(() => {
                const emailUserData = getCurrentEmailUserData();
                if (emailUserData.isEmailUser) {
                    return (
                        <div className="bg-blue-900 border border-blue-700 rounded-lg p-4">
                            <h4 className="font-medium text-blue-200 mb-2">Текущий Email пользователь</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <span className="text-blue-300">Lead ID:</span>
                                    <span className="text-white ml-2">{emailUserData.leadId || 'N/A'}</span>
                                </div>
                                <div>
                                    <span className="text-blue-300">Email ID:</span>
                                    <span className="text-white ml-2">{emailUserData.emailId || 'N/A'}</span>
                                </div>
                                <div>
                                    <span className="text-blue-300">Кампания:</span>
                                    <span className="text-white ml-2">{emailUserData.campaign || 'N/A'}</span>
                                </div>
                                <div>
                                    <span className="text-blue-300">Источник:</span>
                                    <span className="text-white ml-2">{emailUserData.source || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    );
                }
                return null;
            })()}

            {/* Быстрые действия */}
            <div className="bg-gray-800 rounded-lg shadow border border-gray-700 p-6">
                <h3 className="text-lg font-medium text-white mb-4">Быстрые действия</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <button
                        onClick={handleCreateQuickEmail}
                        className="flex items-center justify-center p-4 bg-orange-600 hover:bg-orange-700 rounded-lg text-white transition-colors"
                    >
                        <MessageSquare className="h-5 w-5 mr-2" />
                        Быстрое письмо
                    </button>
                    <button
                        onClick={handleCreateCampaign}
                        className="flex items-center justify-center p-4 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors"
                    >
                        <Mail className="h-5 w-5 mr-2" />
                        Новая кампания
                    </button>
                    <button
                        onClick={handleCreateContact}
                        className="flex items-center justify-center p-4 bg-green-600 hover:bg-green-700 rounded-lg text-white transition-colors"
                    >
                        <UserPlus className="h-5 w-5 mr-2" />
                        Добавить контакт
                    </button>
                    <button
                        onClick={handleCreateSender}
                        className="flex items-center justify-center p-4 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-colors"
                    >
                        <User className="h-5 w-5 mr-2" />
                        Новый отправитель
                    </button>
                </div>
            </div>

            {/* Статистика по отправителям */}
            {analytics.senders_stats && analytics.senders_stats.length > 0 && (
                <div className="bg-gray-800 rounded-lg shadow border border-gray-700 p-6">
                    <h3 className="text-lg font-medium text-white mb-4">Статистика по отправителям</h3>
                    <div className="space-y-4">
                        {analytics.senders_stats.map(sender => (
                            <div key={sender.id} className="flex items-center justify-between p-4 border border-gray-600 rounded-lg">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <User className="h-8 w-8 text-gray-400" />
                                    </div>
                                    <div className="ml-4">
                                        <p className="font-medium text-white">{sender.display_name || sender.name}</p>
                                        <p className="text-sm text-gray-400">{sender.email}</p>
                                        {sender.is_default && (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                                <Star className="h-3 w-3 mr-1" />
                                                По умолчанию
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-medium text-white">{sender.campaigns_count || 0} кампаний</p>
                                    <p className="text-xs text-gray-400">
                                        {sender.total_sent || 0} отправлено, {sender.total_opened || 0} открыто
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Последние кампании */}
            <div className="bg-gray-800 rounded-lg shadow border border-gray-700">
                <div className="px-6 py-4 border-b border-gray-700">
                    <h3 className="text-lg font-medium text-white">Последние кампании</h3>
                </div>
                <div className="p-6">
                    {analytics.recent_campaigns?.length > 0 ? (
                        <div className="space-y-4">
                            {analytics.recent_campaigns.map(campaign => (
                                <div key={campaign.id} className="flex items-center justify-between p-4 border border-gray-600 rounded-lg">
                                    <div className="flex items-center space-x-4">
                                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[campaign.status]}`}>
                                            {campaign.status}
                                        </div>
                                        <div>
                                            <p className="font-medium text-white">{campaign.name}</p>
                                            <p className="text-sm text-gray-400">{campaignTypes[campaign.campaign_type]}</p>
                                            <p className="text-xs text-gray-500">От: {campaign.sender_name || campaign.sender_email}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-medium text-white">{campaign.open_rate}% открытий</p>
                                        <p className="text-xs text-gray-400">{campaign.total_recipients} получателей</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-400 text-center py-8">Нет кампаний для отображения</p>
                    )}
                </div>
            </div>
        </div>
    );

    const renderAnalytics = () => (
        <div className="space-y-6 text-white">
            <h2 className="text-2xl font-bold text-white">Аналитика кампаний</h2>

            <div className="bg-gray-800 rounded-lg shadow p-6 border border-gray-700">
                <h3 className="text-lg font-medium text-white mb-4">Статистика по типам кампаний</h3>

                {analytics.campaigns_by_type?.length > 0 ? (
                    <div className="space-y-4">
                        {analytics.campaigns_by_type.map(type => (
                            <div key={type.campaign_type} className="flex items-center justify-between p-4 border border-gray-600 rounded-lg">
                                <div>
                                    <p className="font-medium text-white">{campaignTypes[type.campaign_type]}</p>
                                    <p className="text-sm text-gray-400">{type.count} кампаний</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-gray-300">Отправлено: {type.total_sent || 0}</p>
                                    <p className="text-sm text-gray-300">Открыто: {type.total_opened || 0}</p>
                                    <p className="text-sm text-gray-300">Кликов: {type.total_clicked || 0}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-400 text-center py-8">Нет данных для отображения</p>
                )}
            </div>

            {/* ОБНОВЛЕНО: Блок с интеграцией Google Analytics */}
            <div className="bg-gray-800 rounded-lg shadow p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-white">Интеграция с Google Analytics</h3>
                    <div className="flex space-x-2">
                        <a
                            href="https://analytics.google.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-400 hover:text-orange-300 flex items-center"
                            onClick={() => trackEmailEvent('ga4_link_click', { event_category: 'external_links' })}
                        >
                            <BarChart3 className="h-4 w-4 mr-1" />
                            Открыть GA4
                        </a>
                        <button
                            onClick={() => {
                                trackEmailEvent('analytics_test', {
                                    test_timestamp: new Date().toISOString(),
                                    event_category: 'testing'
                                });
                                showSuccessMessage('Тестовое событие отправлено в GA4');
                            }}
                            className="text-green-400 hover:text-green-300 text-sm"
                        >
                            Тест GA4
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border border-gray-600 rounded-lg">
                        <h4 className="font-medium text-white">Отслеживаемые события кампаний</h4>
                        <ul className="mt-2 text-sm text-gray-300 space-y-1">
                            <li>• campaign_created - создание кампаний</li>
                            <li>• campaign_sent - отправка кампаний</li>
                            <li>• quick_email_sent - быстрые письма</li>
                            <li>• email_link_click - клики по ссылкам</li>
                            <li>• template_used - использование шаблонов</li>
                        </ul>
                    </div>

                    <div className="p-4 border border-gray-600 rounded-lg">
                        <h4 className="font-medium text-white">Конверсии</h4>
                        <ul className="mt-2 text-sm text-gray-300 space-y-1">
                            <li>• contact_created - создание контакта</li>
                            <li>• contacts_imported - импорт контактов</li>
                            <li>• campaign_conversion - конверсии от кампаний</li>
                            <li>• email_user_engagement - вовлеченность</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-4 p-4 bg-green-900 border border-green-700 rounded-lg">
                    <h4 className="font-medium text-green-200">Статус интеграции</h4>
                    <div className="mt-2 text-sm text-green-200">
                        <p>✅ Google Analytics 4 настроен</p>
                        <p>✅ UTM параметры отслеживаются</p>
                        <p>✅ Email события трекуются</p>
                        <p>✅ Конверсии настроены</p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderQuickEmails = () => (
        <div className="space-y-6 text-white">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Быстрые письма</h2>
                <button
                    onClick={handleCreateQuickEmail}
                    className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-600"
                >
                    <Plus className="h-4 w-4" />
                    <span>Новое письмо</span>
                </button>
            </div>

            {quickEmails.length > 0 ? (
                <div className="bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-700">
                    <table className="min-w-full divide-y divide-gray-700">
                        <thead className="bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Письмо
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Получатель
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Отправитель
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Статус
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Действия
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-gray-800 divide-y divide-gray-700">
                        {quickEmails.map(email => (
                            <tr key={email.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <div className="text-sm font-medium text-white">{email.subject}</div>
                                        <div className="text-sm text-gray-400">
                                            {new Date(email.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-white">
                                        {email.recipient_name && <div>{email.recipient_name}</div>}
                                        <div className="text-gray-400">{email.recipient_email}</div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                    {email.sender_name || email.sender_email}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[email.status]}`}>
                                        {email.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                    {email.status === 'draft' && (
                                        <button
                                            onClick={() => handleSendQuickEmail(email.id)}
                                            className="text-green-400 hover:text-green-300"
                                        >
                                            <Send className="h-4 w-4" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleEditQuickEmail(email)}
                                        className="text-blue-400 hover:text-blue-300"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteQuickEmail(email.id)}
                                        className="text-red-400 hover:text-red-300"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="bg-gray-800 rounded-lg shadow p-8 text-center border border-gray-700">
                    <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-white">Нет быстрых писем</h3>
                    <p className="mt-1 text-sm text-gray-400">Создайте первое быстрое письмо</p>
                    <div className="mt-6">
                        <button
                            onClick={handleCreateQuickEmail}
                            className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-600 mx-auto"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Создать письмо</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    // Вспомогательные функции для обработчиков
    const handleSendQuickEmail = async (emailId) => {
        try {
            await sendQuickEmail(token, emailId);
            showSuccessMessage('Письмо отправлено');
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при отправке письма: ' + err.message);
        }
    };

    const handleEditQuickEmail = (email) => {
        setEditingItem(email);
        setQuickEmailForm({
            sender: email.sender?.toString() || '',
            recipient_email: email.recipient_email,
            recipient_name: email.recipient_name,
            subject: email.subject,
            html_content: email.html_content,
            text_content: email.text_content
        });
        setShowQuickEmailModal(true);
    };

    const handleDeleteQuickEmail = async (emailId) => {
        if (!window.confirm('Вы уверены, что хотите удалить это письмо?')) {
            return;
        }

        try {
            await deleteQuickEmail(token, emailId);
            showSuccessMessage('Письмо удалено');
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при удалении письма: ' + err.message);
        }
    };

    const renderCampaigns = () => (
        <div className="space-y-6 text-white">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Email кампании</h2>
                <button
                    onClick={handleCreateCampaign}
                    className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-600"
                >
                    <Plus className="h-4 w-4" />
                    <span>Создать кампанию</span>
                </button>
            </div>

            {campaigns.length > 0 ? (
                <div className="bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-700">
                    <table className="min-w-full divide-y divide-gray-700">
                        <thead className="bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Кампания
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Отправитель
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Статус
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Получатели
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Статистика
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Действия
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-gray-800 divide-y divide-gray-700">
                        {campaigns.map(campaign => {
                            const StatusIcon = statusIcons[campaign.status];
                            return (
                                <tr key={campaign.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div>
                                            <div className="text-sm font-medium text-white">{campaign.name}</div>
                                            <div className="text-sm text-gray-400">{campaignTypes[campaign.campaign_type]}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-white">{campaign.sender_name || campaign.sender_email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <StatusIcon className="h-4 w-4 mr-2 text-gray-400" />
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[campaign.status]}`}>
                                                {campaign.status}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                        {campaign.recipients_count || campaign.total_recipients || 0}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                        <div>
                                            <div>Open: {campaign.open_rate}%</div>
                                            <div>Click: {campaign.click_rate}%</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                        <button
                                            onClick={() => handleEditCampaign(campaign)}
                                            className="text-blue-400 hover:text-blue-300"
                                        >
                                            <Edit2 className="h-4 w-4" />
                                        </button>

                                        {campaign.status === 'draft' && (
                                            <>
                                                <button
                                                    onClick={() => {
                                                        setSelectedCampaign(campaign);
                                                        setShowRecipientsModal(true);
                                                    }}
                                                    className="text-green-400 hover:text-green-300"
                                                >
                                                    <Users className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleSendCampaign(campaign.id)}
                                                    className="text-orange-400 hover:text-orange-300"
                                                >
                                                    <Send className="h-4 w-4" />
                                                </button>
                                            </>
                                        )}

                                        <button
                                            onClick={() => handleDeleteCampaign(campaign.id)}
                                            className="text-red-400 hover:text-red-300"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="bg-gray-800 rounded-lg shadow p-8 text-center border border-gray-700">
                    <Mail className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-white">Нет кампаний</h3>
                    <p className="mt-1 text-sm text-gray-400">Создайте свою первую email кампанию</p>
                    <div className="mt-6">
                        <button
                            onClick={handleCreateCampaign}
                            className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-600 mx-auto"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Создать кампанию</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    const renderContacts = () => (
        <div className="space-y-6 text-white">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Контакты</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setShowContactImportModal(true)}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-600"
                    >
                        <Upload className="h-4 w-4" />
                        <span>Импорт</span>
                    </button>
                    <button
                        onClick={handleCreateContact}
                        className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-600"
                    >
                        <Plus className="h-4 w-4" />
                        <span>Новый контакт</span>
                    </button>
                </div>
            </div>

            {/* Фильтры */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                        <input
                            type="text"
                            placeholder="Поиск по имени, email или компании"
                            value={contactFilters.search}
                            onChange={(e) => setContactFilters({...contactFilters, search: e.target.value})}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                        />
                    </div>
                    <div>
                        <select
                            value={contactFilters.segment}
                            onChange={(e) => setContactFilters({...contactFilters, segment: e.target.value})}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="">Все сегменты</option>
                            {segments.map(segment => (
                                <option key={segment} value={segment}>{segment}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <select
                            value={contactFilters.industry}
                            onChange={(e) => setContactFilters({...contactFilters, industry: e.target.value})}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="">Все отрасли</option>
                            {industries.map(industry => (
                                <option key={industry} value={industry}>{industry}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <select
                            value={contactFilters.status}
                            onChange={(e) => setContactFilters({...contactFilters, status: e.target.value})}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="">Все статусы</option>
                            <option value="active">Активный</option>
                            <option value="unsubscribed">Отписался</option>
                            <option value="bounced">Недоступен</option>
                            <option value="invalid">Некорректный</option>
                        </select>
                    </div>
                    <div>
                        <button
                            onClick={() => setContactFilters({search: '', segment: '', industry: '', status: '', tags: ''})}
                            className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-500"
                        >
                            Сбросить
                        </button>
                    </div>
                </div>
            </div>

            {contacts.length > 0 ? (
                <div className="bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-700">
                    <table className="min-w-full divide-y divide-gray-700">
                        <thead className="bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                <input
                                    type="checkbox"
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedContacts(contacts.map(c => c.id));
                                        } else {
                                            setSelectedContacts([]);
                                        }
                                    }}
                                />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Контакт
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Компания
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Сегмент
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Статус
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                Действия
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-gray-800 divide-y divide-gray-700">
                        {contacts.map(contact => (
                            <tr key={contact.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        checked={selectedContacts.includes(contact.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedContacts([...selectedContacts, contact.id]);
                                            } else {
                                                setSelectedContacts(selectedContacts.filter(id => id !== contact.id));
                                            }
                                        }}
                                    />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <div className="text-sm font-medium text-white">{contact.name || 'Без имени'}</div>
                                        <div className="text-sm text-gray-400">{contact.email}</div>
                                        {contact.tag_list && contact.tag_list.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {contact.tag_list.slice(0, 3).map(tag => (
                                                    <span key={tag} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">
                                                        {tag}
                                                    </span>
                                                ))}
                                                {contact.tag_list.length > 3 && (
                                                    <span className="px-2 py-1 text-xs bg-gray-600 text-white rounded">
                                                        +{contact.tag_list.length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-white">{contact.company || '-'}</div>
                                    {contact.position && (
                                        <div className="text-sm text-gray-400">{contact.position}</div>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-white">{contact.segment || '-'}</div>
                                    <div className="text-sm text-gray-400">{contact.industry || '-'}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        contact.status === 'active' ? 'bg-green-100 text-green-800' :
                                            contact.status === 'unsubscribed' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                    }`}>
                                        {contact.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                    <button
                                        onClick={() => handleEditContact(contact)}
                                        className="text-blue-400 hover:text-blue-300"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteContact(contact.id)}
                                        className="text-red-400 hover:text-red-300"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="bg-gray-800 rounded-lg shadow p-8 text-center border border-gray-700">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-white">Нет контактов</h3>
                    <p className="mt-1 text-sm text-gray-400">Добавьте первый контакт или импортируйте из файла</p>
                    <div className="mt-6 flex justify-center space-x-3">
                        <button
                            onClick={handleCreateContact}
                            className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-600"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Добавить контакт</span>
                        </button>
                        <button
                            onClick={() => setShowContactImportModal(true)}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-600"
                        >
                            <Upload className="h-4 w-4" />
                            <span>Импорт</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Панель действий для выбранных контактов */}
            {selectedContacts.length > 0 && (
                <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 border border-gray-700 rounded-lg p-4 shadow-lg">
                    <div className="flex items-center space-x-4">
                        <span className="text-white">{selectedContacts.length} выбрано</span>
                        <button
                            onClick={() => {
                                // Логика добавления выбранных контактов в кампанию
                                setShowRecipientsModal(true);
                            }}
                            className="bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700"
                        >
                            Добавить в кампанию
                        </button>
                        <button
                            onClick={() => setSelectedContacts([])}
                            className="bg-gray-600 text-white px-3 py-2 rounded hover:bg-gray-700"
                        >
                            Отменить
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    const renderSenders = () => (
        <div className="space-y-6 text-white">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Отправители</h2>
                <button
                    onClick={handleCreateSender}
                    className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-600"
                >
                    <Plus className="h-4 w-4" />
                    <span>Новый отправитель</span>
                </button>
            </div>

            {senders.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {senders.map(sender => (
                        <div key={sender.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <User className="h-10 w-10 text-gray-400" />
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-lg font-medium text-white">
                                            {sender.display_name || sender.name}
                                        </h3>
                                        <p className="text-sm text-gray-400">{sender.email}</p>
                                        {sender.is_default && (
                                            <div className="mt-2">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                                    <Star className="h-3 w-3 mr-1" />
                                                    По умолчанию
                                                </span>
                                            </div>
                                        )}
                                        {sender.is_verified && (
                                            <div className="mt-1">
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                                    <CheckCircle className="h-3 w-3 mr-1" />
                                                    Подтвержден
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex space-x-2">
                                    {!sender.is_default && (
                                        <button
                                            onClick={() => handleSetDefaultSender(sender.id)}
                                            className="text-yellow-400 hover:text-yellow-300"
                                            title="Установить по умолчанию"
                                        >
                                            <Star className="h-4 w-4" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleEditSender(sender)}
                                        className="text-blue-400 hover:text-blue-300"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteSender(sender.id)}
                                        className="text-red-400 hover:text-red-300"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-gray-800 rounded-lg shadow p-8 text-center border border-gray-700">
                    <User className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-white">Нет отправителей</h3>
                    <p className="mt-1 text-sm text-gray-400">Добавьте первого отправителя для отправки писем</p>
                    <div className="mt-6">
                        <button
                            onClick={handleCreateSender}
                            className="bg-orange-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-600 mx-auto"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Добавить отправителя</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    // Дополнительные обработчики
    const handleDeleteCampaign = async (campaignId) => {
        if (!window.confirm('Вы уверены, что хотите удалить эту кампанию?')) {
            return;
        }

        try {
            await deleteEmailCampaign(token, campaignId);
            showSuccessMessage('Кампания удалена');
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при удалении кампании: ' + err.message);
        }
    };

    const handleEditContact = (contact) => {
        setEditingItem(contact);
        setContactForm({
            email: contact.email,
            name: contact.name,
            company: contact.company,
            phone: contact.phone,
            position: contact.position,
            website: contact.website,
            industry: contact.industry,
            segment: contact.segment,
            tags: contact.tags,
            notes: contact.notes
        });
        setShowContactModal(true);
    };

    const handleDeleteContact = async (contactId) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот контакт?')) {
            return;
        }

        try {
            await deleteEmailContact(token, contactId);
            showSuccessMessage('Контакт удален');
            loadContacts();
        } catch (err) {
            showErrorMessage('Ошибка при удалении контакта: ' + err.message);
        }
    };

    const handleEditSender = (sender) => {
        setEditingItem(sender);
        setSenderForm({
            name: sender.name,
            email: sender.email,
            display_name: sender.display_name,
            is_default: sender.is_default
        });
        setShowSenderModal(true);
    };

    const handleDeleteSender = async (senderId) => {
        if (!window.confirm('Вы уверены, что хотите удалить этого отправителя?')) {
            return;
        }

        try {
            await deleteEmailSender(token, senderId);
            showSuccessMessage('Отправитель удален');
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при удалении отправителя: ' + err.message);
        }
    };

    const handleSetDefaultSender = async (senderId) => {
        try {
            await setDefaultSender(token, senderId);
            showSuccessMessage('Отправитель установлен по умолчанию');
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при установке отправителя по умолчанию: ' + err.message);
        }
    };

    const handleAddRecipients = async () => {
        if (!selectedCampaign) return;

        setIsLoading(true);
        try {
            if (recipientsMode === 'manual') {
                const recipients = parseRecipientsFromText(recipientsText);
                if (recipients.length === 0) {
                    showErrorMessage('Не удалось распознать получателей из введенного текста');
                    return;
                }

                await addRecipientsToEmailCampaign(token, selectedCampaign.id, recipients);
                showSuccessMessage(`Добавлено ${recipients.length} получателей`);
                setRecipientsText('');
            } else if (recipientsMode === 'contacts') {
                if (selectedContacts.length === 0) {
                    showErrorMessage('Не выбраны контакты');
                    return;
                }

                await addRecipientsFromContacts(token, selectedCampaign.id, selectedContacts);
                showSuccessMessage(`Добавлено ${selectedContacts.length} получателей из контактов`);
                setSelectedContacts([]);
            }

            setShowRecipientsModal(false);
            loadInitialData();
        } catch (err) {
            showErrorMessage('Ошибка при добавлении получателей: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="h-full flex flex-col bg-gray-900">
            {/* Уведомления */}
            {error && (
                <div className="mb-4 p-3 bg-red-900 text-red-200 rounded-lg flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    {error}
                </div>
            )}

            {success && (
                <div className="mb-4 p-3 bg-green-900 text-green-200 rounded-lg flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    {success}
                </div>
            )}

            {/* Табы */}
            <div className="flex border-b border-gray-700 mb-6 overflow-x-auto">
                {[
                    { key: 'dashboard', label: 'Дашборд' },
                    { key: 'quick-emails', label: 'Быстрые письма' },
                    { key: 'campaigns', label: 'Кампании' },
                    { key: 'contacts', label: 'Контакты' },
                    { key: 'senders', label: 'Отправители' },
                    { key: 'analytics', label: 'Аналитика' }
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => handleTabChange(tab.key)}
                        className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
                            activeTab === tab.key
                                ? 'border-b-2 border-orange-500 text-orange-400'
                                : 'text-gray-400 hover:text-gray-300'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Контент */}
            <div className="flex-1 overflow-y-auto p-6">
                {isLoading && (
                    <div className="flex items-center justify-center h-64">
                        <RefreshCw className="animate-spin h-8 w-8 text-orange-500" />
                    </div>
                )}

                {!isLoading && activeTab === 'dashboard' && renderDashboard()}
                {!isLoading && activeTab === 'analytics' && renderAnalytics()}
                {!isLoading && activeTab === 'quick-emails' && renderQuickEmails()}
                {!isLoading && activeTab === 'campaigns' && renderCampaigns()}
                {!isLoading && activeTab === 'contacts' && renderContacts()}
                {!isLoading && activeTab === 'senders' && renderSenders()}
            </div>

            {/* Модальные окна */}

            {/* Модальное окно создания/редактирования кампании */}
            {showCampaignModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-600">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-white">
                                {editingItem ? 'Редактировать кампанию' : 'Создать кампанию'}
                            </h3>
                            <button onClick={() => setShowCampaignModal(false)}>
                                <XCircle className="h-6 w-6 text-gray-400 hover:text-gray-300" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Название кампании *
                                    </label>
                                    <input
                                        type="text"
                                        value={campaignForm.name}
                                        onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="Название кампании"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Тип кампании
                                    </label>
                                    <select
                                        value={campaignForm.campaign_type}
                                        onChange={(e) => {
                                            setCampaignForm({...campaignForm, campaign_type: e.target.value});
                                            loadCampaignTemplate(e.target.value);
                                        }}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                    >
                                        {Object.entries(campaignTypes).map(([value, label]) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Отправитель *
                                    </label>
                                    <select
                                        value={campaignForm.sender}
                                        onChange={(e) => setCampaignForm({...campaignForm, sender: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                    >
                                        <option value="">Выберите отправителя</option>
                                        {senders.map(sender => (
                                            <option key={sender.id} value={sender.id.toString()}>
                                                {sender.display_name || sender.name} ({sender.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Тема письма *
                                    </label>
                                    <input
                                        type="text"
                                        value={campaignForm.subject}
                                        onChange={(e) => setCampaignForm({...campaignForm, subject: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="Тема письма"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        UTM Campaign
                                    </label>
                                    <input
                                        type="text"
                                        value={campaignForm.utm_campaign}
                                        onChange={(e) => setCampaignForm({...campaignForm, utm_campaign: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="Автоматически из названия"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        UTM Content
                                    </label>
                                    <input
                                        type="text"
                                        value={campaignForm.utm_content}
                                        onChange={(e) => setCampaignForm({...campaignForm, utm_content: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="email_link"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    HTML контент
                                </label>
                                <textarea
                                    value={campaignForm.html_content}
                                    onChange={(e) => setCampaignForm({...campaignForm, html_content: e.target.value})}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 h-40 text-white focus:ring-2 focus:ring-orange-500"
                                    placeholder="HTML содержимое письма"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Используйте переменные: {`{{name}}, {{company}}, {{tracking_url}}, {{unsubscribe_url}}`}
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Текстовый контент *
                                </label>
                                <textarea
                                    value={campaignForm.text_content}
                                    onChange={(e) => setCampaignForm({...campaignForm, text_content: e.target.value})}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 h-32 text-white focus:ring-2 focus:ring-orange-500"
                                    placeholder="Текстовая версия письма"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <button
                                onClick={() => setShowCampaignModal(false)}
                                className="px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700"
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleSaveCampaign}
                                disabled={isLoading}
                                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                            >
                                {isLoading ? 'Сохранение...' : 'Сохранить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Модальное окно быстрого письма */}
            {showQuickEmailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-600">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-white">
                                {editingItem ? 'Редактировать письмо' : 'Быстрое письмо'}
                            </h3>
                            <button onClick={() => setShowQuickEmailModal(false)}>
                                <XCircle className="h-6 w-6 text-gray-400 hover:text-gray-300" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Отправитель *
                                    </label>
                                    <select
                                        value={quickEmailForm.sender}
                                        onChange={(e) => setQuickEmailForm({...quickEmailForm, sender: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                    >
                                        <option value="">Выберите отправителя</option>
                                        {senders.map(sender => (
                                            <option key={sender.id} value={sender.id.toString()}>
                                                {sender.display_name || sender.name} ({sender.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Шаблон
                                    </label>
                                    <select
                                        onChange={(e) => e.target.value && loadQuickEmailTemplate(e.target.value)}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                    >
                                        <option value="">Выберите шаблон</option>
                                        <option value="introduction">Знакомство</option>
                                        <option value="follow_up">Следующий шаг</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Email получателя *
                                    </label>
                                    <input
                                        type="email"
                                        value={quickEmailForm.recipient_email}
                                        onChange={(e) => setQuickEmailForm({...quickEmailForm, recipient_email: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="recipient@example.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Имя получателя
                                    </label>
                                    <input
                                        type="text"
                                        value={quickEmailForm.recipient_name}
                                        onChange={(e) => setQuickEmailForm({...quickEmailForm, recipient_name: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="Имя получателя"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Тема письма *
                                </label>
                                <input
                                    type="text"
                                    value={quickEmailForm.subject}
                                    onChange={(e) => setQuickEmailForm({...quickEmailForm, subject: e.target.value})}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                    placeholder="Тема письма"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    HTML контент
                                </label>
                                <textarea
                                    value={quickEmailForm.html_content}
                                    onChange={(e) => setQuickEmailForm({...quickEmailForm, html_content: e.target.value})}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 h-32 text-white focus:ring-2 focus:ring-orange-500"
                                    placeholder="HTML версия письма (необязательно)"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Текстовый контент *
                                </label>
                                <textarea
                                    value={quickEmailForm.text_content}
                                    onChange={(e) => setQuickEmailForm({...quickEmailForm, text_content: e.target.value})}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 h-32 text-white focus:ring-2 focus:ring-orange-500"
                                    placeholder="Текст письма"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <button
                                onClick={() => setShowQuickEmailModal(false)}
                                className="px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700"
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleSaveQuickEmail}
                                disabled={isLoading}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                            >
                                {isLoading ? 'Сохранение...' : 'Сохранить'}
                            </button>
                            <button
                                onClick={handleSendQuickEmailDirect}
                                disabled={isLoading}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                            >
                                {isLoading ? 'Отправка...' : 'Отправить сразу'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Модальное окно отправителя */}
            {showSenderModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl border border-gray-600">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-white">
                                {editingItem ? 'Редактировать отправителя' : 'Новый отправитель'}
                            </h3>
                            <button onClick={() => setShowSenderModal(false)}>
                                <XCircle className="h-6 w-6 text-gray-400 hover:text-gray-300" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Имя *
                                    </label>
                                    <input
                                        type="text"
                                        value={senderForm.name}
                                        onChange={(e) => setSenderForm({...senderForm, name: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="Ваше имя"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={senderForm.email}
                                        onChange={(e) => setSenderForm({...senderForm, email: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="your@email.com"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Отображаемое имя
                                </label>
                                <input
                                    type="text"
                                    value={senderForm.display_name}
                                    onChange={(e) => setSenderForm({...senderForm, display_name: e.target.value})}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                    placeholder="Имя, которое увидит получатель"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Если не указано, будет использовано обычное имя
                                </p>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="is_default"
                                    checked={senderForm.is_default}
                                    onChange={(e) => setSenderForm({...senderForm, is_default: e.target.checked})}
                                    className="rounded border-gray-600 bg-gray-700 text-orange-600 focus:ring-orange-500"
                                />
                                <label htmlFor="is_default" className="ml-2 text-sm text-gray-300">
                                    Использовать по умолчанию
                                </label>
                            </div>

                            <div className="bg-blue-900 border border-blue-700 rounded-lg p-4">
                                <p className="text-sm text-blue-200">
                                    <strong>Важно:</strong> Убедитесь, что email адрес подтвержден в Azure Communication Services.
                                    Неподтвержденные адреса могут не работать для отправки писем.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <button
                                onClick={() => setShowSenderModal(false)}
                                className="px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700"
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleSaveSender}
                                disabled={isLoading}
                                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                            >
                                {isLoading ? 'Сохранение...' : 'Сохранить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Модальное окно контакта */}
            {showContactModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-600">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-white">
                                {editingItem ? 'Редактировать контакт' : 'Новый контакт'}
                            </h3>
                            <button onClick={() => setShowContactModal(false)}>
                                <XCircle className="h-6 w-6 text-gray-400 hover:text-gray-300" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={contactForm.email}
                                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="contact@example.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Имя
                                    </label>
                                    <input
                                        type="text"
                                        value={contactForm.name}
                                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="Имя контакта"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Компания
                                    </label>
                                    <input
                                        type="text"
                                        value={contactForm.company}
                                        onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="Название компании"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Должность
                                    </label>
                                    <input
                                        type="text"
                                        value={contactForm.position}
                                        onChange={(e) => setContactForm({...contactForm, position: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="Должность"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Телефон
                                    </label>
                                    <input
                                        type="tel"
                                        value={contactForm.phone}
                                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="+7 (xxx) xxx-xx-xx"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Веб-сайт
                                    </label>
                                    <input
                                        type="url"
                                        value={contactForm.website}
                                        onChange={(e) => setContactForm({...contactForm, website: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="https://example.com"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Отрасль
                                    </label>
                                    <input
                                        type="text"
                                        value={contactForm.industry}
                                        onChange={(e) => setContactForm({...contactForm, industry: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="IT, Финансы, etc."
                                        list="industries"
                                    />
                                    <datalist id="industries">
                                        {industries.map(industry => (
                                            <option key={industry} value={industry} />
                                        ))}
                                    </datalist>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Сегмент
                                    </label>
                                    <input
                                        type="text"
                                        value={contactForm.segment}
                                        onChange={(e) => setContactForm({...contactForm, segment: e.target.value})}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder="enterprise, startup, etc."
                                        list="segments"
                                    />
                                    <datalist id="segments">
                                        {segments.map(segment => (
                                            <option key={segment} value={segment} />
                                        ))}
                                    </datalist>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Теги
                                </label>
                                <input
                                    type="text"
                                    value={contactForm.tags}
                                    onChange={(e) => setContactForm({...contactForm, tags: e.target.value})}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                                    placeholder="тег1, тег2, тег3"
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Разделяйте теги запятыми
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Заметки
                                </label>
                                <textarea
                                    value={contactForm.notes}
                                    onChange={(e) => setContactForm({...contactForm, notes: e.target.value})}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 h-20 text-white focus:ring-2 focus:ring-orange-500"
                                    placeholder="Дополнительная информация о контакте"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <button
                                onClick={() => setShowContactModal(false)}
                                className="px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700"
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleSaveContact}
                                disabled={isLoading}
                                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                            >
                                {isLoading ? 'Сохранение...' : 'Сохранить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Модальное окно добавления получателей */}
            {showRecipientsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-600">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-white">
                                Добавить получателей в "{selectedCampaign?.name}"
                            </h3>
                            <button onClick={() => setShowRecipientsModal(false)}>
                                <XCircle className="h-6 w-6 text-gray-400 hover:text-gray-300" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Табы для выбора способа добавления */}
                            <div className="flex border-b border-gray-700">
                                <button
                                    onClick={() => setRecipientsMode('manual')}
                                    className={`px-4 py-2 font-medium text-sm ${recipientsMode === 'manual' ? 'border-b-2 border-orange-500 text-orange-400' : 'text-gray-400 hover:text-gray-300'}`}
                                >
                                    Ручной ввод
                                </button>
                                <button
                                    onClick={() => setRecipientsMode('contacts')}
                                    className={`px-4 py-2 font-medium text-sm ${recipientsMode === 'contacts' ? 'border-b-2 border-orange-500 text-orange-400' : 'text-gray-400 hover:text-gray-300'}`}
                                >
                                    Из контактов
                                </button>
                            </div>

                            {recipientsMode === 'manual' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Список получателей
                                    </label>
                                    <textarea
                                        value={recipientsText}
                                        onChange={(e) => setRecipientsText(e.target.value)}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 h-40 text-white focus:ring-2 focus:ring-orange-500"
                                        placeholder={`Формат: email, имя, компания, lead_id, отрасль, сегмент
Пример:
client@company.com, Иван Петров, TechCorp, lead_001, IT, enterprise
manager@startup.com, Мария Сидорова, StartupLab, lead_002, fintech, startup

Минимально нужен только email. Остальные поля необязательны.`}
                                    />
                                    <p className="text-sm text-gray-400 mt-1">
                                        Каждый получатель с новой строки. Поля разделяются запятыми.
                                        Обязательно только поле email.
                                    </p>
                                </div>
                            )}

                            {recipientsMode === 'contacts' && (
                                <div>
                                    <p className="text-sm text-gray-300 mb-4">
                                        Выбрано контактов: {selectedContacts.length}
                                    </p>
                                    {selectedContacts.length === 0 && (
                                        <p className="text-gray-400 text-center py-8">
                                            Выберите контакты во вкладке "Контакты", затем вернитесь сюда
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <button
                                onClick={() => setShowRecipientsModal(false)}
                                className="px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700"
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleAddRecipients}
                                disabled={isLoading || (recipientsMode === 'manual' && !recipientsText.trim()) || (recipientsMode === 'contacts' && selectedContacts.length === 0)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                            >
                                {isLoading ? 'Добавление...' : 'Добавить получателей'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Модальное окно импорта контактов */}
            {showContactImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-3xl border border-gray-600">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-white">
                                Импорт контактов
                            </h3>
                            <button onClick={() => setShowContactImportModal(false)}>
                                <XCircle className="h-6 w-6 text-gray-400 hover:text-gray-300" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Данные контактов
                                </label>
                                <textarea
                                    value={contactImportText}
                                    onChange={(e) => setContactImportText(e.target.value)}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 h-40 text-white focus:ring-2 focus:ring-orange-500"
                                    placeholder={`Формат: email, имя, компания, телефон, должность, отрасль, сегмент, теги, заметки
Пример:
contact@company.com, Иван Петров, TechCorp, +7-xxx-xxx-xxxx, CTO, IT, enterprise, vip, Важный клиент
manager@startup.com, Мария Сидорова, StartupLab, , CEO, fintech, startup, , 

Минимально нужен только email. Остальные поля могут быть пустыми.`}
                                />
                                <p className="text-sm text-gray-400 mt-1">
                                    Каждый контакт с новой строки. Поля разделяются запятыми.
                                    Обязательно только поле email.
                                </p>
                            </div>

                            <div className="bg-blue-900 border border-blue-700 rounded-lg p-4">
                                <h4 className="font-medium text-blue-200 mb-2">Порядок полей:</h4>
                                <ol className="text-sm text-blue-200 space-y-1">
                                    <li>1. Email (обязательно)</li>
                                    <li>2. Имя</li>
                                    <li>3. Компания</li>
                                    <li>4. Телефон</li>
                                    <li>5. Должность</li>
                                    <li>6. Отрасль</li>
                                    <li>7. Сегмент</li>
                                    <li>8. Теги (через запятую)</li>
                                    <li>9. Заметки</li>
                                </ol>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 mt-6">
                            <button
                                onClick={() => setShowContactImportModal(false)}
                                className="px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700"
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleBulkImportContacts}
                                disabled={isLoading || !contactImportText.trim()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading ? 'Импорт...' : 'Импортировать контакты'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailCampaignManager;